import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LayoutDashboard, ChefHat, CalendarDays, ShoppingBag, Grid3X3, Users, Clock, Bell, LogOut, Utensils, Menu, X, FileText, Settings, MonitorSmartphone } from 'lucide-react';
import { api } from '../../contexts/AuthContext';
import { getApiBase } from '../../lib/api';
import { toast } from 'sonner';


const navGroups = [
  {
    id: 'pilotage',
    items: [
      { to: '/admin', icon: LayoutDashboard, label: 'Tableau de bord', end: true },
      { to: '/admin/dishes', icon: ChefHat, label: 'Plats & Menu' },
      { to: '/admin/tables', icon: Grid3X3, label: 'Tables & QR' },
    ],
  },
  {
    id: 'demandes',
    items: [
      { to: '/admin/orders', icon: ShoppingBag, label: 'Commandes' },
      { to: '/admin/reservations', icon: CalendarDays, label: 'Reservations' },
      { to: '/admin/quotes', icon: FileText, label: 'Devis' },
    ],
  },
  {
    id: 'equipe',
    items: [
      { external: true, href: '/pointage', icon: MonitorSmartphone, label: 'Station de pointage' },
      { to: '/admin/employees', icon: Users, label: 'Employes' },
      { to: '/admin/attendance', icon: Clock, label: 'Presence' },
    ],
  },
  {
    id: 'config',
    items: [
      { to: '/admin/settings', icon: Settings, label: 'Parametres' },
    ],
  },
];

const ORDER_ALERT_SOUND = '/sounds/order_priority_alert.wav';

function playAdminNotificationFallback() {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    const audioContext = new AudioContextClass();
    const notes = [
      [0, 0.19, 784], [0.22, 0.19, 988], [0.44, 0.26, 1319],
      [0.96, 0.17, 988], [1.15, 0.3, 1480],
    ];
    const now = audioContext.currentTime;
    notes.forEach(([start, duration, frequency]) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, now + start);
      gainNode.gain.setValueAtTime(0.0001, now + start);
      gainNode.gain.exponentialRampToValueAtTime(0.17, now + start + 0.012);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, now + start + duration);
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.start(now + start);
      oscillator.stop(now + start + duration + 0.02);
    });
    window.setTimeout(() => audioContext.close().catch(() => {}), 1700);
  } catch {}
}

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [mobileNav, setMobileNav] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const audioEnabledRef = useRef(false);
  const orderSoundRef = useRef(null);
  const activeRequestAlertsRef = useRef([]);
  const knownNotificationIdsRef = useRef(new Set());
  const notificationsInitializedRef = useRef(false);
  const unreadCount = notifications.filter(n => !n.read).length;

  const createOrderSound = useCallback(() => {
    if (!orderSoundRef.current) {
      const audio = new Audio(ORDER_ALERT_SOUND);
      audio.preload = 'auto';
      audio.volume = 1;
      orderSoundRef.current = audio;
    }
    return orderSoundRef.current;
  }, []);

  const activateAudio = useCallback(async () => {
    const sound = createOrderSound();
    sound.pause();
    sound.currentTime = 0;
    sound.loop = false;
    // Browsers require a real user gesture before future notification audio
    // can play. Start it inaudibly for a short moment; no alert is heard here.
    sound.muted = false;
    sound.volume = 0;
    try {
      await sound.play();
      window.setTimeout(() => {
        sound.pause();
        sound.currentTime = 0;
        sound.volume = 1;
      }, 250);
      audioEnabledRef.current = true;
      setAudioEnabled(true);
    } catch {
      sound.volume = 1;
    }
  }, [createOrderSound]);

  useEffect(() => {
    const enableAudio = () => {
      activateAudio();
      document.removeEventListener('pointerdown', enableAudio, true);
      document.removeEventListener('keydown', enableAudio, true);
    };
    document.addEventListener('pointerdown', enableAudio, true);
    document.addEventListener('keydown', enableAudio, true);
    return () => {
      document.removeEventListener('pointerdown', enableAudio, true);
      document.removeEventListener('keydown', enableAudio, true);
      orderSoundRef.current?.pause();
    };
  }, [activateAudio]);

  const playAdminNotificationSound = useCallback(async ({ repeat = false } = {}) => {
    if (!audioEnabledRef.current) return;

    try {
      const sound = createOrderSound();
      sound.pause();
      sound.currentTime = 0;
      sound.muted = false;
      sound.volume = 1;
      sound.loop = repeat;
      await sound.play();
    } catch {
      playAdminNotificationFallback();
    }
  }, [createOrderSound]);

  const stopAdminNotificationSound = useCallback(() => {
    const sound = orderSoundRef.current;
    if (!sound) return;
    sound.loop = false;
    sound.pause();
    sound.currentTime = 0;
  }, []);

  const handleIncomingOrderNotification = useCallback(async (notification) => {
    if (!notification || knownNotificationIdsRef.current.has(notification.id)) {
      return;
    }

    knownNotificationIdsRef.current.add(notification.id);
    window.dispatchEvent(new CustomEvent('admin-notification', { detail: notification }));
    api.get(`/dashboard/stats`).then(r => setStats(r.data)).catch(() => {});

    const notificationLabels = {
      order: 'Nouvelle commande',
      reservation: 'Nouvelle reservation',
      quote: 'Nouveau devis',
    };
    if (notificationLabels[notification.type]) {
      toast.success(`${notification.title || notificationLabels[notification.type]}: ${notification.message}`);
      await playAdminNotificationSound({ repeat: true });
    }
  }, [playAdminNotificationSound]);

  const activeRequestAlerts = notifications.filter((notification) => (
    !notification.read && ['order', 'reservation', 'quote'].includes(notification.type)
  ));

  useEffect(() => {
    activeRequestAlertsRef.current = activeRequestAlerts;
  }, [activeRequestAlerts]);

  useEffect(() => {
    const handleRequestResolution = (event) => {
      const { type, referenceKey, referenceId } = event.detail || {};
      if (!type || !referenceKey || !referenceId) return;
      const remainingAlerts = activeRequestAlertsRef.current.filter((notification) => (
        notification.type !== type || (notification.data || {})[referenceKey] !== referenceId
      ));
      activeRequestAlertsRef.current = remainingAlerts;
      if (remainingAlerts.length === 0) stopAdminNotificationSound();
      setNotifications((current) => current.map((notification) => (
        notification.type === type && (notification.data || {})[referenceKey] === referenceId
          ? { ...notification, read: true }
          : notification
      )));
    };
    window.addEventListener('admin-request-resolved', handleRequestResolution);
    return () => window.removeEventListener('admin-request-resolved', handleRequestResolution);
  }, [stopAdminNotificationSound]);

  useEffect(() => {
    if (!audioEnabled || activeRequestAlerts.length === 0) {
      stopAdminNotificationSound();
      return undefined;
    }

    // The notification sound loops until the request is handled.
    playAdminNotificationSound({ repeat: true });
    return undefined;
  }, [activeRequestAlerts.length, audioEnabled, playAdminNotificationSound, stopAdminNotificationSound]);

  useEffect(() => {
    if (!loading && (!user || (user.role !== 'admin' && user.role !== 'manager'))) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const fetchNotifs = () => {
      api.get(`/notifications?limit=20`).then(async (r) => {
        const nextNotifications = Array.isArray(r.data) ? r.data : [];
        setNotifications(nextNotifications);

        if (!notificationsInitializedRef.current) {
          knownNotificationIdsRef.current = new Set(nextNotifications.map(notification => notification.id));
          notificationsInitializedRef.current = true;
          return;
        }

        for (const notification of nextNotifications) {
          if (!knownNotificationIdsRef.current.has(notification.id)) {
            await handleIncomingOrderNotification(notification);
          }
        }
      }).catch(() => {});
      api.get(`/dashboard/stats`).then(r => setStats(r.data)).catch(() => {});
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 10000);
    return () => clearInterval(interval);
  }, [handleIncomingOrderNotification]);

  useEffect(() => {
    const API_BASE = getApiBase();
    const wsUrl = `${API_BASE.replace('https', 'wss').replace('http', 'ws')}/api/ws/notifications`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'notification') {
            setNotifications(prev => [msg.data, ...prev]);
            handleIncomingOrderNotification(msg.data);
          } else if (msg.type === 'notification_resolved') {
            const data = msg.data || {};
            window.dispatchEvent(new CustomEvent('admin-request-resolved', {
              detail: {
                type: data.request_type,
                referenceKey: data.reference_key,
                referenceId: data.reference_id,
              },
            }));
          }
        } catch {}
      };
    } catch {}
    return () => { if (ws) ws.close(); };
  }, [handleIncomingOrderNotification]);

  if (loading) return <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center"><p>Chargement...</p></div>;

  const pendingOrders = stats?.pending_orders || 0;
  const pendingReservations = stats?.pending_reservations || 0;
  const pendingQuotes = stats?.pending_quotes || 0;
  const totalPending = pendingOrders + pendingReservations + pendingQuotes;

  const navCounts = {
    '/admin/orders': pendingOrders,
    '/admin/reservations': pendingReservations,
    '/admin/quotes': pendingQuotes,
    '/admin': totalPending,
  };

  const renderNavItem = (item, mobile = false) => {
    const baseClass = mobile
      ? 'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[#FAF8F5]/70'
      : 'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors text-[#FAF8F5]/70 hover:text-white hover:bg-[#FAF8F5]/5';

    if (item.external) {
      return (
        <a
          key={item.label}
          href={item.href}
          target="_blank"
          rel="noreferrer"
          onClick={mobile ? () => setMobileNav(false) : undefined}
          className={baseClass}
          data-testid={`admin-nav-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
        >
          <item.icon className={mobile ? 'w-4 h-4' : 'w-4.5 h-4.5'} />
          <span className="flex-1">{item.label}</span>
        </a>
      );
    }

    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        onClick={mobile ? () => setMobileNav(false) : undefined}
        className={({ isActive }) => `${baseClass} ${isActive ? 'bg-[#5B6B3F] text-white font-medium' : ''}`}
        data-testid={`admin-nav-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
      >
        <item.icon className={mobile ? 'w-4 h-4' : 'w-4.5 h-4.5'} />
        <span className="flex-1">{item.label}</span>
        {!!navCounts[item.to] && (
          <span className={`ml-auto min-w-5 h-5 px-1.5 rounded-full bg-[#8C3A3A] text-white text-[11px] leading-5 text-center ${mobile ? '' : 'shadow-sm'}`}>
            {navCounts[item.to]}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <div className="admin-typography min-h-screen bg-[#FAF8F5] flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:w-64 flex-col bg-[#2A2A28] text-[#FAF8F5] fixed h-full z-30">
        <div className="p-5 border-b border-[#FAF8F5]/10">
          <div className="flex items-center gap-2">
            <Utensils className="w-5 h-5 text-[#D4AF37]" />
            <span className="font-heading text-base font-semibold">Le Traiteur Libanais</span>
          </div>
          <p className="text-xs text-[#FAF8F5]/50 mt-1">Administration</p>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-4">
          {navGroups.map((group) => (
            <div key={group.id} className="rounded-2xl border border-white/5 bg-white/[0.02] p-2">
              <div className="space-y-1">
                {group.items.map((item) => renderNavItem(item))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-[#FAF8F5]/10">
          <button onClick={async () => { await logout(); navigate('/login'); }} className="flex items-center gap-2 px-3 py-2 text-sm text-[#FAF8F5]/60 hover:text-white w-full rounded-xl hover:bg-[#FAF8F5]/5 transition-colors" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Deconnexion
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-[#2A2A28] text-white z-40 px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => setMobileNav(!mobileNav)}>{mobileNav ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
          <span className="font-heading text-sm font-semibold">Admin</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && <span className="absolute -top-1.5 -right-1.5 bg-[#8C3A3A] text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">{unreadCount}</span>}
          </div>
        </div>
      </div>

      {mobileNav && (
        <div className="lg:hidden fixed inset-0 z-30 bg-black/50" onClick={() => setMobileNav(false)}>
          <div className="w-64 h-full overflow-y-auto bg-[#2A2A28] text-[#FAF8F5] p-4 pt-16 space-y-4" onClick={e => e.stopPropagation()}>
            {navGroups.map((group) => (
              <div key={group.id} className="rounded-2xl border border-white/5 bg-white/[0.02] p-2">
                <div className="space-y-1">
                  {group.items.map((item) => renderNavItem(item, true))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
